import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button, { ButtonType } from '../general-components/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket } from '@fortawesome/free-solid-svg-icons';
import styles from './VideoUploadPage.module.css';
import { uploadVideoWithFiles } from '../../services/apiService';
import LoadingRing from '../ui-components/LoadingRing';
import Modal, { ModalInfo } from '../manage-user/Modal';
import FormInput from '../general-components/FormInput';
import FileSelector from '../files/FileSelector';

const VideoUploadPage: React.FC = () => {
    const { t } = useTranslation();
    // States to update from the form elements
    const [title, setTitle] = useState<string>('');
    const [tags, setTags] = useState<string[]>([]);
    const [author, setAuthor] = useState<string>('');
    const [videoFileId, setVideoFileId] = useState<string>('');
    const [thumbnailFileId, setThumbnailFileId] = useState<string>('');

    // States to manage component state/view
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isShowModal, setIsShowModal] = useState<boolean>(false);
    const [isUploaded, setIsUploaded] = useState<boolean>(false);
    const [modalMessage, setModalMessage] = useState('');

    // Predefined modal content for successful and unsuccessful files uploading
    const modalInfoError: ModalInfo = {
        title: t('videoUpload.videoUploadPage.modals.error.title'),
        message: modalMessage,
    };
    const modalInfoUploaded: ModalInfo = {
        title: t('videoUpload.videoUploadPage.modals.success.title'),
        message: modalMessage,
    };

    const refreshPage = () => {
        window.location.reload();
    };

    // Function to handle form submission
    const onFormSubmit = async (
        e: React.FormEvent<HTMLFormElement>,
        isPublished?: true
    ) => {
        e.preventDefault();
        // Check if all required information provided
        if (!videoFileId || !thumbnailFileId || !title || !author) {
            showModal(t('videoUpload.videoUploadPage.messages.fillAllFields'));
            return;
        }
        setIsLoading(true);

        const metaData = {
            title: title.trim(),
            tags: tags.map((tag) => tag.trim()),
            author: author.trim(),
            videoFileId,
            thumbnailFileId,
            published: !!isPublished,
        };

        try {
            // Upload video metadata with file IDs
            const response = await uploadVideoWithFiles(metaData);

            if (response.status !== 200) {
                showModal(
                    response.data.message ||
                        t(
                            'videoUpload.videoUploadPage.messages.failedToCreateVideo'
                        )
                );
                setIsLoading(false);
                return;
            }

            resetForm();
            setIsUploaded(true);
            showModal(
                t('videoUpload.videoUploadPage.messages.thanksForContribution')
            );
        } catch (error) {
            showModal(t('videoUpload.videoUploadPage.messages.uploadFailed'));
            resetForm();
        }

        setIsLoading(false);
    };

    const resetForm = () => {
        setTitle('');
        setAuthor('');
        setTags([]);
        setVideoFileId('');
        setThumbnailFileId('');
    };

    const showModal = (message: string) => {
        setModalMessage(message);
        setIsShowModal(true);
    };

    // Function to reset some component states, refresh page if submission was successful
    const updateView = () => {
        isUploaded && refreshPage();
        setIsUploaded(false);
        setIsShowModal(false);
    };

    return (
        <>
            <h1 className={styles['page-title']}>
                {t('videoUpload.videoUploadPage.title')}
            </h1>
            <form onSubmit={onFormSubmit} className={styles.form}>
                <FormInput
                    label={t('videoUpload.videoUploadPage.form.title.label')}
                    name="title"
                    type="text"
                    defaultValue={title}
                    placeholder={t(
                        'videoUpload.videoUploadPage.form.title.placeholder'
                    )}
                    onChange={(e) => setTitle(e.target.value)}
                    errorMsg=""
                />
                <FormInput
                    label={t('videoUpload.videoUploadPage.form.tags.label')}
                    name="tags"
                    type="text"
                    defaultValue={tags.join(',')}
                    placeholder={t(
                        'videoUpload.videoUploadPage.form.tags.placeholder'
                    )}
                    onChange={(e) => setTags(e.target.value.split(','))}
                    errorMsg=""
                />
                <FormInput
                    label={t('videoUpload.videoUploadPage.form.author.label')}
                    name="author-name"
                    type="text"
                    defaultValue={author}
                    placeholder={t(
                        'videoUpload.videoUploadPage.form.author.placeholder'
                    )}
                    onChange={(e) => setAuthor(e.target.value)}
                    errorMsg=""
                />

                <FileSelector
                    label={t(
                        'videoUpload.videoUploadPage.form.videoFile.label'
                    )}
                    onFilesSelected={(fileIds) => setVideoFileId(fileIds[0])}
                    allowMultiple={false}
                    allowedTypes={{
                        document: false,
                        image: false,
                        video: true,
                    }}
                    ownerType="department"
                />

                <FileSelector
                    label={t(
                        'videoUpload.videoUploadPage.form.thumbnailImage.label'
                    )}
                    onFilesSelected={(fileIds) =>
                        setThumbnailFileId(fileIds[0])
                    }
                    allowMultiple={false}
                    allowedTypes={{
                        document: false,
                        image: true,
                        video: false,
                    }}
                    ownerType="department"
                />

                <div className={styles['form-controllers-container']}>
                    {/* <Button
                        onClick={(e: React.FormEvent<HTMLFormElement>) => {
                            onFormSubmit(e);
                        }}
                        type={ButtonType.primary}
                        className={styles['save-button']}
                    >
                        Save
                    </Button> */}
                    <Button
                        onClick={(e: React.FormEvent<HTMLFormElement>) => {
                            onFormSubmit(e, true);
                        }}
                        type={ButtonType.submit}
                        className={styles['upload-button']}
                    >
                        {t('videoUpload.videoUploadPage.buttons.publish')}
                    </Button>
                </div>
                {isLoading && (
                    <div className={styles['backdrop']}>
                        <LoadingRing size="large" />
                    </div>
                )}
                {isShowModal && !isUploaded && (
                    <Modal updateView={updateView} info={modalInfoError}>
                        <Button
                            onClick={() => {
                                updateView();
                            }}
                            type={ButtonType.primary}
                            small={true}
                        >
                            <span className={styles['button-shape']}>
                                {t('videoUpload.videoUploadPage.buttons.ok')}
                            </span>
                        </Button>
                    </Modal>
                )}
                {isShowModal && isUploaded && !isLoading && (
                    <Modal updateView={updateView} info={modalInfoUploaded}>
                        <Button
                            onClick={() => {
                                updateView();
                            }}
                            type={ButtonType.primary}
                            small={true}
                        >
                            <span className={styles['button-shape']}>
                                {t('videoUpload.videoUploadPage.buttons.ok')}
                            </span>
                        </Button>
                    </Modal>
                )}
            </form>
        </>
    );
};

export default VideoUploadPage;
